'use client'

import { useHasCart } from '@/feature/cart/hooks/useHasCart'
import { useCartId } from '@/feature/cart/hooks/useCartId'
import { useAddToCartTracking } from '@/feature/cart/services/cart.tracking'
import { trackAddToCartInLocalStorage } from '@/feature/cart/services/cart.tracking.addToCart'
import { useAddProductToCart } from '@/feature/product/hooks/useAddProductToCart'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { useToast } from '@/ui/toast/Toast'
import { createNativeToastIntent } from '@/ui/toast/toast.intents'
import type { ProductCard_AddToCartFragment } from '@gql-generated/graphql'
import { atom, useAtom } from 'jotai'

// Global mutex so only one quick add-to-cart runs at a time across product cards
export const isAddingProductToCartAtom = atom(false)

const DEFAULT_SOURCE = 'plot produit'

type UseProductCardAddToCartParams = {
  // Product to add, dedicated fragment spread into ProductCard_ProductPriceAndStock
  product: ProductCard_AddToCartFragment
  // Analytics source, reuses the ProductCard source prop
  source?: string
}

type UseProductCardAddToCartResult = {
  addToCart: () => void
  isLoading: boolean
  isDisabled: boolean
  canAddToCart: boolean
  // Quantity added, the QuantityPicker minimum/step
  quantity: number
}

export function useProductCardAddToCart({
  product,
  source,
}: UseProductCardAddToCartParams): UseProductCardAddToCartResult {
  const cartId = useCartId()
  const hasCart = useHasCart(cartId)
  const { t } = useTranslation(['generic'])
  const toast = useToast()
  const pushAddToCartEvent = useAddToCartTracking()
  const [addProductToCart, { loading: isAddingToCart }] = useAddProductToCart()
  const [isAddingProductToCart, setIsAddingProductToCart] = useAtom(isAddingProductToCartAtom)

  const quantity =
    product.stock?.packaging?.minimalSalesQuantity ??
    product.stock?.packaging?.incrementQuantity ??
    product.stock?.packaging?.quantity ??
    1

  const analyticsSource = source ?? DEFAULT_SOURCE
  const canAddToCart = hasCart && !!product.canAddToCart

  const addToCart = () => {
    if (!canAddToCart) {
      return
    }

    setIsAddingProductToCart(true)
    addProductToCart({
      variables: {
        reference: product.reference,
        quantity,
        cartId,
      },
      onCompleted: () => {
        handleIntent(
          createNativeToastIntent('success', `add-to-cart-${product.reference}`, {
            title: t('generic:addToCart.success.title'),
            message: t('generic:addToCart.success.message'),
          }),
          {
            whenInBrowser: () => {
              trackAddToCartInLocalStorage([product.reference], analyticsSource)
              pushAddToCartEvent([
                {
                  reference: product.reference,
                  quantity,
                  source: analyticsSource,
                  price: (product.price?.exclTax ?? 0) * quantity,
                },
              ])
              toast.success({
                dedupe: false,
                title: t('generic:addToCart.success.title'),
                message: t('generic:addToCart.success.message'),
              })
            },
          },
        )
        setIsAddingProductToCart(false)
      },
      onError: () => {
        setIsAddingProductToCart(false)
      },
    })
  }

  return {
    addToCart,
    isLoading: isAddingToCart,
    isDisabled: !isAddingToCart && isAddingProductToCart,
    canAddToCart,
    quantity,
  }
}
